import { SopList } from 'components/sop/sop-list/SopList'
import { useEffect, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { API_SOP } from 'services/apiSop'
import { useFetch } from 'services/useFetch'
import { getAllQueryParams, getYears, setQueryParams } from 'utils/utils'
import { ACTION_TYPE } from 'constants/constant'
import { INITIAL_DATA_QUERY_SOP, SOP_FILTER_LIST, SOP_STATUS_OPTIONS } from 'configs/sop/configTableSop'

const INITIAL_FILTER = {
    category_id: [],
    status: [],
    year: '',
}

const getFilterFromQuery = (queryObj) => {
    const category_id = queryObj?.category_id
        ? (Array.isArray(queryObj.category_id)
              ? queryObj.category_id
              : String(queryObj.category_id).split(',').filter(Boolean))
        : []

    const status = queryObj?.status
        ? (Array.isArray(queryObj.status)
              ? queryObj.status
              : String(queryObj.status).split(',').filter(Boolean))
        : []

    const year = queryObj?.year
        ? isNaN(Number(queryObj.year))
            ? queryObj.year
            : Number(queryObj.year)
        : ''

    return {
        category_id,
        status,
        year,
    }
}

const countFilterLength = (filterObj) => {
    let totalLength = 0
    for (const key in filterObj) {
        if (Object.prototype.hasOwnProperty.call(filterObj, key)) {
            if (Array.isArray(filterObj[key])) {
                totalLength += filterObj[key].length
            } else if (filterObj[key]) {
                totalLength += 1
            }
        }
    }
    return totalLength
}

export const SopListContainer = () => {
    const location = useLocation()
    const navigate = useNavigate()
    const [searchParams, setSearchParams] = useSearchParams()
    const query = searchParams.size > 0 ? getAllQueryParams(searchParams) : INITIAL_DATA_QUERY_SOP

    const [selectedItems, setSelectedItems] = useState([])
    const [deleteId, setDeleteId] = useState(null)
    const [archiveIds, setArchiveIds] = useState([])
    const [previewData, setPreviewData] = useState(null)
    const [showFilter, setShowFilter] = useState(false)
    const [activeFilter, setActiveFilter] = useState(SOP_FILTER_LIST[0])
    const [filter, setFilter] = useState(() => getFilterFromQuery(query))
    const [filterCount, setFilterCount] = useState(() => countFilterLength(getFilterFromQuery(query)))
    const [categoryOptions, setCategoryOptions] = useState([])
    const [modal, setModal] = useState({
        preview: false,
        delete: false,
        archive: false,
    })
    const [toast, setToast] = useState({
        isShowToast: false,
        message: '',
    })

    const { data: sopList, fetching: getSopList, isLoading } = useFetch(API_SOP.LIST)

    const { fetching: deleteSop, isLoading: isDeleting } = useFetch(API_SOP.DELETE)
    const { fetching: archiveSop, isLoading: isArchiving } = useFetch(API_SOP.ARCHIVE)
    const { fetching: getCategoryFilter } = useFetch(API_SOP.CATEGORY_FILTER)

    useEffect(() => {
        fetchSopList()
        const currentFilter = getFilterFromQuery(query)
        setFilter(currentFilter)
        setFilterCount(countFilterLength(currentFilter))
    }, [location?.search])

    useEffect(() => {
        fetchCategoryOptions()
    }, [])

    const fetchCategoryOptions = async () => {
        try {
            const response = await getCategoryFilter({})
            setCategoryOptions(response?.data)
        } catch (error) {
            setCategoryOptions([])
        }
    }

    const fetchSopList = () => {
        const payload = {
            page: Number(query?.page) || 1,
            limit: Number(query?.limit) || 20,
            search: query?.search || '',
            sop_category_ids: query?.category_id
                ? Array.isArray(query.category_id)
                    ? query.category_id
                    : query.category_id.split(',').filter(Boolean)
                : [],
            status: query?.status
                ? Array.isArray(query.status)
                    ? query.status
                    : query.status.split(',').filter(Boolean)
                : [],
            ...(query?.year ? { year_created: Number(query.year) } : {}),
        }
        getSopList(payload)
    }

    const onChangeQuery = (queryParams) => {
        const params = setQueryParams({ ...query, ...queryParams })
        setSearchParams(params, { replace: true })
    }

    const clearToast = () => {
        setToast({ isShowToast: false, message: '' })
    }

    const showToast = (message) => {
        setToast({ isShowToast: true, message })
    }

    const onCreateHandler = () => {
        navigate('/sop-settings/create-sop')
    }

    const onClickItemHandler = ({ item, actionType: type }) => {
        switch (type) {
            case ACTION_TYPE.EDIT:
                navigate(`/sop-settings/update-sop?sopId=${item?.id}`)
                break
            case ACTION_TYPE.PREVIEW:
                setPreviewData(item)
                setModal((prev) => ({ ...prev, preview: true }))
                break
            case ACTION_TYPE.DELETE:
                setDeleteId([item?.id])
                setModal((prev) => ({ ...prev, delete: true }))
                break
            case ACTION_TYPE.ARCHIVE:
                setArchiveIds([item?.id])
                setModal((prev) => ({ ...prev, archive: true }))
                break
            default:
                break
        }
    }

    const onDelete = () => {
        deleteSop({ sop_ids: deleteId }).then(() => {
            showToast('SOP berhasil dihapus')
            setModal((prev) => ({ ...prev, delete: false }))
            setDeleteId(null)
            setSelectedItems([])
            if (sopList?.data?.results?.length === 1 && Number(query?.page) !== 1) {
                onChangeQuery({ page: Number(query?.page) - 1 })
            } else {
                fetchSopList()
            }
        })
    }

    const onArchive = () => {
        archiveSop({ sop_ids: archiveIds }).then(() => {
            showToast('SOP berhasil diarsipkan')
            setModal((prev) => ({ ...prev, archive: false }))
            setArchiveIds([])
            setSelectedItems([])
            fetchSopList()
        })
    }

    const onBulkArchive = () => {
        const activeItems = selectedItems.filter((item) => item?.status === 'active')
        setArchiveIds(activeItems.map((item) => item.id))
        setModal((prev) => ({ ...prev, archive: true }))
    }

    const onBulkDelete = () => {
        const draftItems = selectedItems.filter((item) => item?.status === 'draft')
        setDeleteId(draftItems.map((item) => item.id))
        setModal((prev) => ({ ...prev, delete: true }))
    }

    // Filter handlers
    const onShowFilter = () => setShowFilter(true)
    const onCloseFilter = () => {
        setShowFilter(false)
        const currentFilter = getFilterFromQuery(query)
        setFilter(currentFilter)
        setFilterCount(countFilterLength(currentFilter))
    }
    const onSelectFilter = (item) => setActiveFilter(item)

    const onChangeFilterOption = (e, index) => {
        const { name, value, checked } = e
        const currentValues = Array.isArray(filter[name]) ? filter[name] : []
        if (checked) {
            setFilter({ ...filter, [name]: [...currentValues, String(value)] })
        } else {
            setFilter({ ...filter, [name]: currentValues.filter((v) => v !== String(value)) })
        }
    }

    const onChangeFilterSelect = (selected) => {
        setFilter({ ...filter, [activeFilter.value]: selected?.value?.value })
    }

    const onSubmitFilter = () => {
        const count = countFilterLength(filter)
        setFilterCount(count)

        const filterQuery = {
            category_id: filter.category_id?.length > 0 ? filter.category_id.join(',') : '',
            status: filter.status?.length > 0 ? filter.status.join(',') : '',
            year: filter.year || '',
        }
        onChangeQuery({ page: 1, ...filterQuery })
        setShowFilter(false)
    }

    const onClearFilter = () => {
        setFilter(INITIAL_FILTER)
        setFilterCount(0)
        const { category_id, status, year, ...rest } = query
        const params = setQueryParams({ ...rest, page: 1 })
        setSearchParams(params, { replace: true })
        setShowFilter(false)
    }

    const yearOptions = getYears(2020)

    const filterOptions = {
        category_id: categoryOptions,
        status: SOP_STATUS_OPTIONS,
        year: yearOptions,
    }

    const props = {
        dataList: sopList?.data,
        isLoading,
        query,
        onChangeQuery,
        onClickItemHandler,
        onCreateHandler,
        modal,
        setModal,
        isDeleting,
        isArchiving,
        onDelete,
        onArchive,
        toast,
        clearToast,
        selectedItems,
        onSelectItem: (items) => setSelectedItems([...items]),
        onBulkArchive,
        onBulkDelete,
        previewData,
        // Filter props
        showFilter,
        onShowFilter,
        onCloseFilter,
        filterList: SOP_FILTER_LIST,
        activeFilter,
        onSelectFilter,
        filter,
        filterCount,
        filterOptions,
        onChangeFilterOption,
        onChangeFilterSelect,
        onSubmitFilter,
        onClearFilter,
    }

    return <SopList {...props} />
}
